import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { userAPI, foodItemAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ChefHat,
  Store,
  ShoppingCart,
  Plus,
  Minus,
  Star,
  MapPin,
  Phone,
  Sun,
  Coffee,
  Moon,
  Cookie
} from 'lucide-react';
import BuyerLayout from '../../components/layouts/BuyerLayout';

const FOOD_PLACEHOLDER = "https://images.unsplash.com/photo-1672477179695-7276b0602fa9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHw0fHxpbmRpYW4lMjBmb29kJTIwc3ByZWFkJTIwdG9wJTIwdmlld3xlbnwwfHx8fDE3Njc2MDUxOTl8MA&ixlib=rb-4.1.0&q=85&w=400";

const MEAL_TYPE_CONFIG = {
  breakfast: { label: 'Breakfast', icon: Coffee, color: 'bg-amber-100 text-amber-800' },
  lunch: { label: 'Lunch', icon: Sun, color: 'bg-orange-100 text-orange-800' },
  dinner: { label: 'Dinner', icon: Moon, color: 'bg-indigo-100 text-indigo-800' },
  snacks: { label: 'Snacks', icon: Cookie, color: 'bg-pink-100 text-pink-800' },
  beverages: { label: 'Beverages', icon: Coffee, color: 'bg-cyan-100 text-cyan-800' },
  all_day: { label: 'All Day', icon: Star, color: 'bg-green-100 text-green-800' },
};

export default function SellerDetail() {
  const { sellerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { items: cartItems, addItem, updateQuantity, getItemCount } = useCart();
  
  const [seller, setSeller] = useState(null);
  const [foodItems, setFoodItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSellerData();
  }, [sellerId]);

  const loadSellerData = async () => {
    setLoading(true);
    try {
      // Load seller info and their items
      const [sellerRes, itemsRes] = await Promise.all([
        userAPI.getById(sellerId),
        foodItemAPI.getAll({ seller_id: sellerId })
      ]);
      setSeller(sellerRes.data);
      setFoodItems(itemsRes.data);
    } catch (error) {
      toast.error('Failed to load seller data');
      console.error('Seller load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCartQuantity = (itemId) => {
    const cartItem = cartItems.find(i => i.id === itemId);
    return cartItem?.quantity || 0;
  };

  const handleAddToCart = (item) => {
    addItem(item);
    toast.success(`Added ${item.name} to cart`);
  };

  if (loading) {
    return (
      <BuyerLayout>
        <div className="space-y-6 pb-mobile-nav">
          <Skeleton className="h-10 w-32" />
          <Card>
            <CardContent className="p-6">
              <Skeleton className="h-16 w-16 rounded-full mx-auto mb-4" />
              <Skeleton className="h-6 w-48 mx-auto mb-2" />
              <Skeleton className="h-4 w-32 mx-auto" />
            </CardContent>
          </Card>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="overflow-hidden">
                <Skeleton className="h-40 w-full" />
                <CardContent className="p-4 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </BuyerLayout>
    );
  }

  return (
    <BuyerLayout>
      <div className="space-y-6 pb-mobile-nav" data-testid="seller-detail-page">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/buyer')}
          className="pl-0"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>

        {/* Seller Profile Card */}
        {seller && (
          <Card className="overflow-hidden">
            <div className="bg-gradient-to-r from-primary/20 to-primary/5 p-6">
              <div className="flex flex-col items-center text-center">
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-4 border-4 border-white shadow-lg">
                  {seller.photo_url ? (
                    <img 
                      src={seller.photo_url} 
                      alt={seller.name}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : seller.role === 'seller' ? (
                    <ChefHat className="w-10 h-10 text-primary" />
                  ) : (
                    <Store className="w-10 h-10 text-primary" />
                  )}
                </div>
                <h1 className="font-heading text-2xl font-bold text-foreground mb-1">
                  {seller.name}
                </h1>
                <Badge variant="secondary" className="mb-3">
                  {seller.role === 'seller' ? 'Home Cook' : 'Vendor'}
                </Badge>
                
                {seller.specialization && (
                  <p className="text-sm text-muted-foreground mb-2">
                    <Star className="w-4 h-4 inline mr-1 text-yellow-500" />
                    Specializes in: {seller.specialization}
                  </p>
                )}
                
                {seller.experience && (
                  <p className="text-sm text-muted-foreground">
                    {seller.experience} years of experience
                  </p>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Food Items Section */}
        <section>
          <div className="section-header">
            <h2 className="section-title">
              {seller?.name}&apos;s Menu
            </h2>
            <span className="text-sm text-muted-foreground">
              {foodItems.length} item{foodItems.length !== 1 ? 's' : ''}
            </span>
          </div>

          {foodItems.length === 0 ? (
            <Card className="py-12">
              <CardContent className="text-center">
                <ChefHat className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="font-heading text-lg font-semibold mb-2">No items available</h3>
                <p className="text-muted-foreground text-sm">
                  This cook hasn&apos;t added any items yet
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {foodItems.map((item) => {
                const cartQty = getCartQuantity(item.id);
                const mealConfig = MEAL_TYPE_CONFIG[item.meal_type] || MEAL_TYPE_CONFIG.all_day;
                const MealIcon = mealConfig.icon;
                
                return (
                  <Card key={item.id} className="overflow-hidden card-hover" data-testid={`food-item-${item.id}`}>
                    <div className="relative h-40">
                      <img
                        src={item.image_url || FOOD_PLACEHOLDER}
                        alt={item.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = FOOD_PLACEHOLDER;
                        }}
                      />
                      <div className="absolute top-2 left-2 flex gap-1">
                        <span className={item.is_vegetarian ? 'veg-badge' : 'non-veg-badge'}></span>
                      </div>
                      {item.is_free && (
                        <span className="absolute top-2 right-2 free-badge">FREE</span>
                      )}
                      {/* Meal Type Badge */}
                      <div className={`absolute bottom-2 left-2 flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${mealConfig.color}`}>
                        <MealIcon className="w-3 h-3" />
                        {mealConfig.label}
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-1">
                        <h3 className="font-semibold text-foreground truncate flex-1">{item.name}</h3>
                        <span className="price-tag ml-2">
                          {item.is_free ? 'Free' : `₹${item.price}`}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{item.description}</p>
                      
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="text-xs">
                          {item.category}
                        </Badge>
                        
                        {cartQty > 0 ? (
                          <div className="quantity-selector">
                            <button 
                              onClick={() => updateQuantity(item.id, cartQty - 1)}
                              data-testid={`decrease-${item.id}`}
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span data-testid={`qty-${item.id}`}>{cartQty}</span>
                            <button 
                              onClick={() => updateQuantity(item.id, cartQty + 1)}
                              data-testid={`increase-${item.id}`}
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => handleAddToCart(item)}
                            className="rounded-full"
                            data-testid={`add-to-cart-${item.id}`}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </section>

        {/* Floating Cart Button */}
        {getItemCount() > 0 && (
          <button
            onClick={() => navigate('/buyer/cart')}
            className="fab"
            data-testid="cart-fab"
          >
            <ShoppingCart className="w-6 h-6" />
            <span className="cart-badge">{getItemCount()}</span>
          </button>
        )}
      </div>
    </BuyerLayout>
  );
}
